/** 项目完整工作台：书籍真实封面、五个 Tab（HTML 落地页与执行记录分开）及跨 Tab 持久后台进度。 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  type ProjectChange,
  type ProjectMutation,
  landingProjectQueryKey,
  prefetchLandingProjectWorkbench,
  projectExecutionActive,
  useLandingProject,
  useLandingProjectExecutions,
} from '~/data/queries';
import { landingProjectsApi } from '~/hooks/use-landing-projects';
import { APPLOVIN_MEDIA_SOURCE } from '~/lib/applovin-creative-set-name';
import { bookCodeText } from '~/lib/book-identity';
import { fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { BookCover } from '~/ui/BookCover';
import { CopyableCode } from '~/ui/CopyableCode';
import { ConfirmDialog } from '~/ui/Dialog';
import { LangChip } from '~/ui/LangChip';
import { PageTabs } from '~/ui/PageTabs';
import { DetailSkeleton } from '~/ui/Skeleton';
import { toast } from '~/ui/Toast';
import { LandingProjectBookVisibilityAlert } from './LandingProjectBookVisibilityAlert';
import { LandingProjectChaptersPanel } from './LandingProjectChaptersPanel';
import { LandingProjectCopyDialog } from './LandingProjectCopyDialog';
import { LandingProjectCreativeSetNames } from './LandingProjectCreativeSetNames';
import { LandingProjectEditor } from './LandingProjectEditor';
import { LandingProjectExecutionDrawer } from './LandingProjectExecutionDrawer';
import { LandingProjectExecutionsPanel } from './LandingProjectExecutionsPanel';
import { LandingProjectLandingPagesPanel } from './LandingProjectLandingPagesPanel';
import { LandingProjectMediaPanel } from './LandingProjectMediaPanel';
import { LandingProjectRegenerationPrompt } from './LandingProjectRegenerationPrompt';
import { LandingProjectTemplatePanel } from './LandingProjectTemplatePanel';
import { LandingProjectWebLinksDrawer } from './LandingProjectWebLinksDrawer';
import { ProjectProgressSummary, ProjectState } from './landing-project-display';
/** pages = 生成出的 HTML 落地页；tasks = 本项目执行记录。 */
export type ProjectTab = 'template' | 'chapters' | 'media' | 'pages' | 'tasks';
const projectTabs: readonly ProjectTab[] = ['template', 'chapters', 'media', 'pages', 'tasks'];
/** URL 中的 tab 参数只接受已知值，其余回落到按项目就绪度推导的默认 Tab。 */
export function parseProjectTab(value: string | null): ProjectTab | null {
  return projectTabs.find((tab) => tab === value) ?? null;
}
export function LandingProjectDetail({
  id,
  onBack,
  onOpen,
  tab: controlledTab,
  onTabChange,
}: {
  id: string;
  onBack: () => void;
  onOpen: (id: string) => void;
  /** 传入 onTabChange 时由上层（URL）托管当前 Tab；否则组件内部记忆。 */
  tab?: ProjectTab | null;
  onTabChange?: (tab: ProjectTab) => void;
}) {
  const query = useLandingProject(id);
  const executions = useLandingProjectExecutions(id, 1, {
    detailUpdatedAt: query.dataUpdatedAt,
  });
  const client = useQueryClient();
  const [localTab, setLocalTab] = useState<ProjectTab | null>(null);
  const tab = onTabChange ? (controlledTab ?? null) : localTab;
  const setTab = onTabChange ?? setLocalTab;
  const [editing, setEditing] = useState(false);
  const [copying, setCopying] = useState(false);
  const [webLinksOpen, setWebLinksOpen] = useState(false);
  const [confirmingArchive, setConfirmingArchive] = useState(false);
  const [executionId, setExecutionId] = useState<string | null>(null);
  // 素材卡片「查看落地页」的定位目标；用户手动切 Tab 时清除，避免之后再进 Tab 又被拉回。
  const [focusTaskId, setFocusTaskId] = useState<string | null>(null);
  const [change, setChange] = useState<ProjectChange | null>(null);
  const generation = useMutation({
    mutationFn: (taskIds: string[]) => {
      if (!query.data?.can_generate)
        throw new Error(query.data?.generation_blocked_reason ?? '当前书籍不能投放');
      return landingProjectsApi.execute(id, {
        operation: 'generate',
        ...(taskIds.length ? { task_ids: taskIds } : {}),
      });
    },
    onSuccess: (execution) => {
      setExecutionId(execution.id);
      void client.invalidateQueries({ queryKey: landingProjectQueryKey });
      toast('重新生成已在后台提交，完成后请重新下载', { tone: 'info' });
    },
    onError: (error: Error) => toast(error.message, { tone: 'neg' }),
  });

  // 目标状态由触发的按钮决定，不从可能被轮询刷新的项目状态推导，避免确认归档却发出恢复。
  const archive = useMutation({
    mutationFn: (status: 'ACTIVE' | 'ARCHIVED') => landingProjectsApi.update(id, { status }),
    onSuccess: (result) => changed(result),
    onError: (error: Error) => toast(error.message, { tone: 'neg' }),
  });
  const changed = (result: ProjectMutation) => {
    client.setQueryData([...landingProjectQueryKey, id], result.project);
    void client.invalidateQueries({ queryKey: landingProjectQueryKey });
    if (result.change.html_inputs_changed && result.change.affected_task_count > 0)
      setChange(result.change);
    else toast('已保存', { tone: 'pos' });
  };
  // 与项目详情并行拉任务 / 模板，进入默认 Tab 时不再等详情返回后才开始第二轮请求。
  useEffect(() => prefetchLandingProjectWorkbench(client, id), [client, id]);
  const previousVersion = useRef<number | null>(null);
  useEffect(() => {
    const project = query.data;
    if (!project) return;
    if (
      previousVersion.current !== null &&
      previousVersion.current !== project.config_version &&
      project.tasks.stale > 0
    ) {
      setChange(
        (current) =>
          current ?? {
            html_inputs_changed: true,
            affected_task_ids: [],
            affected_task_count: project.tasks.stale,
            reason: '正文版本或关联生成配置已变化，请更新项目产物',
          },
      );
    }
    previousVersion.current = project.config_version;
  }, [query.data]);
  const active = executions.data?.data.find(projectExecutionActive) ?? query.data?.active_execution;
  const backNavigation = (
    <div className="hstack">
      <button type="button" className="btn btn-ghost btn-sm" onClick={onBack}>
        <Icons.IconChevLeft size={12} />
        返回项目管理
      </button>
    </div>
  );
  if (query.isPending) return <DetailSkeleton onBack={onBack} />;
  if (query.isError || !query.data)
    return (
      <div className="page landing-management">
        {backNavigation}
        <div className="card landing-project-state" role="alert" style={{ marginTop: 14 }}>
          <Icons.IconAlert size={18} style={{ color: 'var(--neg)' }} />
          <span className="landing-project-state-message">
            项目加载失败：{query.error?.message ?? '项目不可用'}
          </span>
          <button type="button" className="btn" onClick={() => void query.refetch()}>
            重新加载
          </button>
        </div>
      </div>
    );
  const project = query.data;
  const selectedTab =
    tab ?? (project.chapters.ready < project.chapters.selected ? 'chapters' : 'pages');
  const tabs: { id: ProjectTab; label: string; count?: number }[] = [
    { id: 'template', label: 'HTML 模板' },
    { id: 'chapters', label: '书籍章节' },
    { id: 'media', label: '媒体素材', count: project.media_count },
    { id: 'pages', label: 'HTML 落地页', count: project.tasks.total },
    { id: 'tasks', label: '任务' },
  ];
  return (
    <div className="page page-enter landing-management">
      {webLinksOpen && (
        <LandingProjectWebLinksDrawer
          key={`${project.novel.id}:${project.media_source}`}
          project={project}
          onClose={() => setWebLinksOpen(false)}
        />
      )}
      {backNavigation}
      <div className="page-header" style={{ flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h1 className="page-title" style={{ overflowWrap: 'anywhere' }}>
            {project.name}
          </h1>
          <div className="page-sub">
            {project.product.name} · {project.media_source}
          </div>
        </div>
        <div className="hstack" style={{ gap: 6, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-sm" onClick={() => setWebLinksOpen(true)}>
            Web 投放链接
          </button>
          <button type="button" className="btn btn-sm" onClick={() => setEditing(true)}>
            编辑信息
          </button>
          <button
            type="button"
            className="btn btn-sm"
            disabled={!project.can_generate}
            title={project.generation_blocked_reason ?? undefined}
            onClick={() => setCopying(true)}
          >
            复制项目
          </button>
          <button
            type="button"
            className="btn btn-sm"
            disabled={archive.isPending}
            onClick={() =>
              project.status === 'ARCHIVED' ? archive.mutate('ACTIVE') : setConfirmingArchive(true)
            }
          >
            {project.status === 'ARCHIVED' ? '恢复项目' : '归档'}
          </button>
        </div>
      </div>
      <LandingProjectBookVisibilityAlert
        warnings={(project.book_visibility_warnings ?? []).map((warning) => ({
          ...warning,
          project_id: project.id,
          project_code: project.code,
          project_name: project.name,
          project_status: project.status,
          novel_id: project.novel.id,
          book_code: project.novel.book_code,
          book_title: project.novel.title,
          cn_title: project.novel.cn_title,
          locale: project.novel.locale,
          product_name: project.product.name,
        }))}
      />
      <div
        className="card hstack"
        style={{ padding: 16, gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}
      >
        <BookCover
          src={project.novel.cover_url}
          alt={`${project.novel.cn_title ?? project.novel.title}封面`}
          width={96}
          height={130}
        />
        <div className="vstack" style={{ gap: 8, flex: 1, minWidth: 240 }}>
          <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
            <ProjectState status={project.status} />
            <CopyableCode value={project.code} label="项目编号" />
          </div>
          <strong style={{ overflowWrap: 'anywhere' }}>
            {project.novel.cn_title ?? project.novel.title}
          </strong>
          <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
            <CopyableCode
              value={bookCodeText({
                bookCode: project.novel.book_code,
                bookGroupCode: project.novel.book_group_code,
              })}
              label="书籍编码"
            />
            {project.novel.locale && <span className="mono">· {project.novel.locale}</span>}
            <LangChip code={project.novel.locale} />
            {project.tag && <span className="chip mono">TAG {project.tag}</span>}
          </div>
          <div className="mono muted">
            AI 审核 {project.chapters.reviewed ?? '—'}/{project.chapters.selected} · AI 脱敏{' '}
            {project.chapters.desensitized ?? '—'}/{project.chapters.selected} · HTML 就绪{' '}
            {project.chapters.ready}/{project.chapters.selected} · 复用 {project.chapters.reused} ·
            素材 {project.media_count} · HTML {project.tasks.generated}/{project.tasks.total}
          </div>
          <div className="mono muted" style={{ fontSize: 'var(--text-xs)' }}>
            创建人 {project.created_by ? (project.created_by.nickname ?? '账号不存在') : '—'} ·
            创建于 {fmtDateTime(project.created_at)}
          </div>
          {project.media_source === APPLOVIN_MEDIA_SOURCE && (
            <LandingProjectCreativeSetNames project={project} />
          )}
          {project.note && (
            <div
              role="note"
              aria-label="项目说明"
              style={{
                padding: '10px 12px',
                borderLeft: '3px solid var(--accent)',
                background: 'var(--accent-soft)',
                borderRadius: 'var(--radius)',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
              }}
            >
              <div className="hstack" style={{ gap: 8 }}>
                <strong>项目说明</strong>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  aria-label="复制项目说明"
                  title="复制项目说明"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(project.note ?? '');
                      toast('已复制项目说明', { tone: 'pos', icon: 'IconCopy' });
                    } catch {
                      toast('复制失败，请手动选择复制', { tone: 'neg' });
                    }
                  }}
                >
                  <Icons.IconCopy size={12} />
                  复制
                </button>
              </div>
              <div style={{ marginTop: 4 }}>{project.note}</div>
            </div>
          )}
          {!project.can_generate && (
            <p role="alert" className="chip chip-warn landing-project-notice">
              {project.generation_blocked_reason ?? '当前书籍不能投放'}
            </p>
          )}
          {project.tasks.stale > 0 && (
            <span className="chip chip-warn landing-project-notice">
              {project.tasks.stale} 个 HTML 落地页需重新生成并重新下载
            </span>
          )}
        </div>
      </div>
      {active && (
        <div className="card-flat hstack" style={{ marginTop: 12, padding: 12, gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <ProjectProgressSummary execution={active} />
          </div>
          <button type="button" className="btn btn-sm" onClick={() => setExecutionId(active.id)}>
            查看进度 / 结果
          </button>
        </div>
      )}
      <PageTabs
        label="项目工作台"
        value={selectedTab}
        items={tabs}
        onChange={(next) => {
          setFocusTaskId(null);
          setTab(next);
        }}
      />
      {selectedTab === 'template' && (
        <LandingProjectTemplatePanel project={project} onChanged={changed} />
      )}
      {selectedTab === 'chapters' && (
        <LandingProjectChaptersPanel
          project={project}
          onChanged={changed}
          onExecution={setExecutionId}
        />
      )}
      {selectedTab === 'media' && (
        <LandingProjectMediaPanel
          project={project}
          onChanged={changed}
          onExecution={setExecutionId}
          onOpenLandingPages={(taskId) => {
            setFocusTaskId(taskId);
            setTab('pages');
          }}
        />
      )}
      {selectedTab === 'pages' && (
        <LandingProjectLandingPagesPanel
          project={project}
          onExecution={setExecutionId}
          focusTaskId={focusTaskId}
        />
      )}
      {selectedTab === 'tasks' && (
        <LandingProjectExecutionsPanel project={project} onExecution={setExecutionId} />
      )}
      {copying && (
        <LandingProjectCopyDialog
          project={project}
          onClose={() => setCopying(false)}
          onSaved={(result) => {
            setCopying(false);
            void client.invalidateQueries({ queryKey: landingProjectQueryKey });
            onOpen(result.project.id);
          }}
        />
      )}
      {editing && (
        <LandingProjectEditor
          project={project}
          onClose={() => setEditing(false)}
          onSaved={(result) => {
            setEditing(false);
            changed(result);
          }}
        />
      )}
      {executionId && (
        <LandingProjectExecutionDrawer
          projectId={id}
          executionId={executionId}
          onClose={() => setExecutionId(null)}
        />
      )}
      {confirmingArchive && (
        <ConfirmDialog
          title="归档项目？"
          message={`归档「${project.name}」后，它不再出现在默认的使用中列表。`}
          details={[
            '任务、HTML 产物、发布地址与章节处理记录全部保留。',
            '可在项目详情中随时恢复。',
          ]}
          tone="warn"
          confirmLabel="归档"
          onConfirm={() => archive.mutate('ARCHIVED')}
          onClose={() => setConfirmingArchive(false)}
        />
      )}
      {change && (
        <LandingProjectRegenerationPrompt
          change={change}
          blockedReason={
            !project.can_generate
              ? (project.generation_blocked_reason ?? '当前书籍不能投放')
              : undefined
          }
          onGenerate={() => generation.mutate([...change.affected_task_ids])}
          onClose={() => setChange(null)}
        />
      )}
    </div>
  );
}
